import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, Typography } from '@mui/material';
import { createRallyeStation, createStation } from '../../api/content';
import { ApiError } from '../../api/client';
import { useAuth } from '../../auth/useAuth';
import type { MultilingualString, StationTips } from '../../api/types';
import ErrorAlert from '../../components/ErrorAlert';
import MultilingualField from '../../components/MultilingualField';
import MapPicker from '../../components/MapPicker';
import StationTipsField from '../../components/StationTipsField';
import BackLink from '../../components/BackLink';

const StationCreate: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation('content');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // When launched from the rallye composition flow, the new station is created
  // AND linked into the rallye in one call (createRallyeStation).
  const rallyeId = searchParams.get('rallyeId');

  const [title, setTitle] = useState<MultilingualString>({});
  const [description, setDescription] = useState<MultilingualString>({});
  const [locationLat, setLocationLat] = useState<number | null>(null);
  const [locationLng, setLocationLng] = useState<number | null>(null);
  const [address, setAddress] = useState('');
  const [tips, setTips] = useState<StationTips | null>(null);
  const [locationPending, setLocationPending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const orgSlug = user?.organization_slug;
  const deTitleMissing = !title.de?.trim();
  // An address without a map position is the wrong-house bug (#547).
  const coordinatesMissing = !!address.trim() && (locationLat === null || locationLng === null);
  const locationBlocked = locationPending || coordinatesMissing;
  const canSubmit = !!orgSlug && !deTitleMissing && !locationBlocked && !submitting;

  const backTo = rallyeId ? `/rallyes/${rallyeId}` : '/stations';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgSlug || deTitleMissing || locationBlocked) return;
    setSubmitting(true);
    setError(null);
    try {
      const body = {
        title,
        description,
        address: address || null,
        location_lat: locationLat,
        location_lng: locationLng,
        tips,
      };
      if (rallyeId) {
        const link = await createRallyeStation(orgSlug, rallyeId, body);
        navigate(`/stations/${link.station.id}`);
      } else {
        const station = await createStation(orgSlug, body);
        navigate(`/stations/${station.id}`);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <BackLink to={backTo} label={t('station.detail.backToList')} />
      <Typography variant="h2" component="h1" gutterBottom>
        {t('station.create.title')}
      </Typography>

      {error && <ErrorAlert message={error} />}

      <Stack spacing={3} sx={{ mt: 3 }}>
        <MultilingualField
          value={title}
          onChange={setTitle}
          label={t('station.create.titleLabel')}
          required
        />
        <MultilingualField
          value={description}
          onChange={setDescription}
          label={t('station.create.descriptionLabel')}
          richText
        />
        <Box>
          <Typography variant="subtitle2" component="div" sx={{ mb: 0.5 }}>
            {t('station.create.locationLabel')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {t('station.create.locationHint')}
          </Typography>
          <MapPicker
            lat={locationLat}
            lng={locationLng}
            address={address}
            onChange={({ lat, lng, address: nextAddress }) => {
              setLocationLat(lat);
              setLocationLng(lng);
              setAddress(nextAddress);
            }}
            disabled={submitting}
            searchLabel={t('station.create.addressLabel')}
            onPendingChange={setLocationPending}
          />
          {locationBlocked && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }} role="alert">
              {t(locationPending ? 'station.location.searchPending' : 'station.location.coordinatesRequired')}
            </Typography>
          )}
        </Box>
        <StationTipsField value={tips} onChange={setTips} disabled={submitting} />
        <Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={() => navigate(backTo)} disabled={submitting}>
            {t('actions.cancel', { ns: 'common' })}
          </Button>
          <Button type="submit" variant="contained" disabled={!canSubmit}>
            {t('station.create.submit')}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default StationCreate;
